/**
 * CrewJobCard.js — COO-composed job card for the Field Specialist route.
 * Renders on top of the FieldSpecialist camera surface with:
 *   - guided walkthrough sequence lock
 *   - tools list (per trade lane)
 *   - zone-sorted staging map
 *   - captures-on-file counter
 */
import React, { useEffect, useState } from "react";
import CooIdentityRibbon from "./CooIdentityRibbon";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CYAN = "#00CFCF"; const CHROME = "#A0A0A0"; const INK = "#F5F5F7";

const card = { background: "rgba(255,255,255,0.03)", border: `1px solid ${CHROME}33`, borderRadius: 12, padding: "clamp(14px,2.5vw,22px)", marginBottom: 16 };
const chip = { display: "inline-block", padding: "4px 10px", borderRadius: 999, border: `1px solid ${CYAN}66`, color: CYAN, fontSize: 11, letterSpacing: "0.1em", marginRight: 6, marginBottom: 6 };
const step = { padding: "10px 12px", borderTop: `1px solid ${CHROME}22`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 13 };

export default function CrewJobCard({ crewEmail, propertyAddress }) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [statuses, setStatuses] = useState({});

  useEffect(() => {
    if (!propertyAddress || !crewEmail) return;
    fetch(`${BACKEND_URL}/api/role-dashboard/crew?crew_email=${encodeURIComponent(crewEmail)}&property_address=${encodeURIComponent(propertyAddress)}`)
      .then(r => r.json()).then(setJob).catch(e => setError(e.message));
  }, [crewEmail, propertyAddress]);

  if (error) return <div style={{ ...card, borderColor: "#f04040" }} data-testid="crew-job-card-error">Job card unavailable: {error}</div>;
  if (!job) return null;

  const mark = (idx, verdict) => {
    setStatuses(s => ({ ...s, [idx]: verdict }));
    if (verdict === "pass" && idx === stepIndex && idx < job.walkthrough_sequence.length - 1) {
      setStepIndex(idx + 1);
    }
  };

  return (
    <div style={card} data-testid="crew-job-card">
      <CooIdentityRibbon />
      <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 6 }}>
        COO-COMPOSED JOB CARD · SEQUENCE LOCKED
      </div>
      <div style={{ display: "grid", gap: 6, fontSize: 12, marginBottom: 12 }}>
        <div><strong>Assigned:</strong> {crewEmail}</div>
        <div><strong>Captures on file:</strong> <span style={{ color: CYAN }}>{job.captures_on_file}</span></div>
      </div>

      <div>
        <div style={{ fontSize: 11, color: CHROME, letterSpacing: "0.15em", marginBottom: 6 }}>WALKTHROUGH SEQUENCE (sequence-lock)</div>
        <div style={{ fontSize: 12, color: CHROME, marginBottom: 8 }}>{job.sequence_lock_rule}</div>
        {job.walkthrough_sequence.map((s, idx) => {
          const locked = idx > stepIndex;
          const verdict = statuses[idx];
          return (
            <div key={idx} style={{ ...step, opacity: locked ? 0.4 : 1 }} data-testid={`crew-step-${idx}`}>
              <div>
                <span style={{ color: CYAN, fontFamily: "monospace" }}>{String(idx + 1).padStart(2, "0")}</span>
                <strong style={{ marginLeft: 8 }}>{s.zone}</strong>
                <span style={{ color: CHROME, marginLeft: 6 }}>· {s.step}</span>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button disabled={locked} onClick={() => mark(idx, "pass")}
                  data-testid={`crew-step-${idx}-pass`}
                  style={{ padding: "4px 10px", border: `1px solid ${verdict === "pass" ? CYAN : CHROME}55`, background: verdict === "pass" ? CYAN : "transparent", color: verdict === "pass" ? "#020A1F" : INK, borderRadius: 6, fontSize: 11, cursor: locked ? "not-allowed" : "pointer" }}>
                  PASS
                </button>
                <button disabled={locked} onClick={() => mark(idx, "fail")}
                  data-testid={`crew-step-${idx}-fail`}
                  style={{ padding: "4px 10px", border: `1px solid ${verdict === "fail" ? "#f04040" : CHROME}55`, background: verdict === "fail" ? "#f04040" : "transparent", color: INK, borderRadius: 6, fontSize: 11, cursor: locked ? "not-allowed" : "pointer" }}>
                  FAIL
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 11, color: CHROME, letterSpacing: "0.15em", marginBottom: 6 }}>ZONE-SORTED STAGING MAP</div>
        {Object.entries(job.staging_map || {}).map(([zone, items]) => (
          <div key={zone} style={{ marginBottom: 8 }} data-testid={`crew-staging-${zone.toLowerCase()}`}>
            <div style={{ fontSize: 12, fontWeight: 700, color: CYAN }}>{zone} <span style={{ color: CHROME, fontWeight: 400 }}>· {items.length} line(s)</span></div>
            {items.slice(0, 4).map((it, i) => (
              <div key={i} style={{ fontSize: 11, color: CHROME, paddingLeft: 12 }}>
                • {it.description} — {it.quantity} {it.unit || ""}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 11, color: CHROME, letterSpacing: "0.15em", marginBottom: 6 }}>COO HINTS</div>
        {(job.coo_hints || []).map((h, i) => <div key={i} style={chip}>{h}</div>)}
      </div>
    </div>
  );
}
